/**************************************************************************
 * Created on Feb 24 2026
 *
 *  # Honeywell Code Usage Policy
 *
 * **Honeywell Source Code Usage Policy Classification:** Highly Confidential
 *
 ********************************************************************************/

import { useState, useRef, useCallback, useEffect } from 'react';
import { refreshToken } from '../api/api';
import { getCookie, ID_TOKEN_KEY, setCookie } from '../../interceptors/constant';
import { IRefreshTokenResponse } from '../models/customer-model';
import useAuth from './useAuth';

export default function useInactivityTimer(timeoutMs: number, activateTimer = true) {
  const [showSessionAlert, setShowSessionAlert] = useState(false);
  const workerRef: any = useRef(null);
  const isReactivatingRef = useRef(false);
  const { setAuth } = useAuth();

  const refreshSessionToken = useCallback(async (): Promise<boolean> => {
    try {
      const refreshTokenResponse: IRefreshTokenResponse = await refreshToken();
      const expiresIn = Number(refreshTokenResponse.expires_in);
      const validExpiry = Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn : 0;

      if (!validExpiry || !refreshTokenResponse.authenticated) {
        return false;
      }

      const userId = refreshTokenResponse.user_id || getCookie(ID_TOKEN_KEY);
      if (userId) {
        setCookie(ID_TOKEN_KEY, userId, validExpiry);
      }

      setAuth({ isAuthenticated: true });
      return true;
    } catch (err) {
      return false;
    }
  }, [setAuth]);

  // Create and clean up the worker only when enabled
  useEffect(() => {
    if (!activateTimer) return;
    
    workerRef.current = new Worker(new URL('../worker/inactivityWorker', import.meta.url));
    
    workerRef.current.onmessage = (e: any) => {
      if (e.data === 'inactive') {
        setShowSessionAlert(true);
      }
    };
    
    return () => {
      if (workerRef.current) {
        workerRef.current.postMessage('stop');
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, [activateTimer, timeoutMs]);

  // Listen to user activity only when not inactive and enabled
  useEffect(() => {
    if (!activateTimer || showSessionAlert) return;
    
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    const reset = () => workerRef.current && workerRef.current.postMessage({ type: 'reset', timeoutMs });
    
    events.forEach(e => window.addEventListener(e, reset));
    reset(); // Initial reset to start the timer
    
    return () => {
      events.forEach(e => window.removeEventListener(e, reset));
      if (workerRef.current) {
        workerRef.current.postMessage('stop');
      }
    };
  }, [showSessionAlert, timeoutMs, activateTimer]);

  const handleReactivate = useCallback(() => {
    if (isReactivatingRef.current) {
      return;
    }

    void (async () => {
      isReactivatingRef.current = true;
      // Hide modal immediately so the countdown timer cannot expire during refresh.
      setShowSessionAlert(false);

      const refreshed = await refreshSessionToken();
      if (!refreshed) {
        setShowSessionAlert(false);
        window.dispatchEvent(new Event('session-expired'));
        isReactivatingRef.current = false;
        return;
      }

      if (workerRef.current) {
        workerRef.current.postMessage({ type: 'reset', timeoutMs });
      }
      isReactivatingRef.current = false;
    })();
  }, [refreshSessionToken, timeoutMs]);

  return { showSessionAlert, handleReactivate };
}
